import { APPLE_AUTH_CONFIG } from '../config/auth';
import { logger } from './logger';

const log = logger.child('appleAuth');

// Capacitor modules are loaded lazily so the web bundle doesn't pull in iOS code.
let Capacitor = null;
let SignInWithApple = null;
let capacitorLoadAttempted = false;

const loadCapacitorModules = async () => {
  if (capacitorLoadAttempted) {
    return { Capacitor, SignInWithApple };
  }
  capacitorLoadAttempted = true;

  try {
    const capacitorCore = await import('@capacitor/core');
    Capacitor = capacitorCore.Capacitor;

    if (Capacitor && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      const mod = await import('@capacitor-community/apple-sign-in');
      SignInWithApple = mod.SignInWithApple;
    }
  } catch (e) {
    log.debug('Capacitor modules not available, web mode');
    Capacitor = { isNativePlatform: () => false, getPlatform: () => 'web' };
    SignInWithApple = null;
  }

  return { Capacitor, SignInWithApple };
};

const isElectron = () => typeof window !== 'undefined' && !!window.electronAPI;

const isWebSdkLoaded = () =>
  typeof window !== 'undefined' && window.AppleID && window.AppleID.auth;

let webInitialized = false;
const initializeAppleWebSdk = () => {
  if (webInitialized || !isWebSdkLoaded()) return;
  window.AppleID.auth.init({
    clientId: APPLE_AUTH_CONFIG.SERVICES_ID,
    scope: APPLE_AUTH_CONFIG.SCOPE,
    redirectURI: APPLE_AUTH_CONFIG.REDIRECT_URI,
    usePopup: true,
  });
  webInitialized = true;
};

/**
 * Sign in with Apple — handles iOS native, Electron, and web.
 * Returns a normalized shape the App.js handler can POST to /api/auth/apple.
 */
export const signInWithApple = async () => {
  // 1. Electron — system browser OAuth via backend relay
  if (isElectron() && window.electronAPI.appleOAuth) {
    try {
      log.debug('Starting Electron Apple OAuth flow');
      const result = await window.electronAPI.appleOAuth();
      if (!result.success) {
        throw new Error(result.error || 'Apple OAuth failed');
      }
      return { identityToken: result.idToken, isNative: false };
    } catch (error) {
      log.error('OAuth failed', error, { phase: 'electron' });
      throw new Error(error.message || 'Apple Sign-In failed');
    }
  }

  // 2. iOS native — Capacitor plugin
  const { Capacitor: Cap, SignInWithApple: SIWA } = await loadCapacitorModules();
  if (Cap && Cap.isNativePlatform() && SIWA) {
    try {
      const result = await SIWA.authorize({
        clientId: APPLE_AUTH_CONFIG.NATIVE_BUNDLE_ID,
        redirectURI: APPLE_AUTH_CONFIG.REDIRECT_URI,
        scopes: APPLE_AUTH_CONFIG.SCOPE,
        state: cryptoRandomState(),
        nonce: cryptoRandomState(),
      });
      log.debug('Native Apple Sign-In result received');
      const r = result?.response || {};
      return {
        identityToken: r.identityToken,
        email: r.email || null,
        name: joinName(r.givenName, r.familyName),
        isNative: true,
        // One-time code the backend exchanges for a revocable refresh token
        // (needed to revoke the Apple grant at account deletion). Native
        // codes carry no redirect URI.
        authorizationCode: r.authorizationCode || null,
      };
    } catch (error) {
      log.error('OAuth failed', error, { phase: 'native' });
      throw new Error(error.message || 'Apple Sign-In failed');
    }
  }

  // 3. Web — Apple JS SDK popup
  if (!isWebSdkLoaded()) {
    throw new Error('Apple Sign-In SDK not loaded');
  }
  initializeAppleWebSdk();
  try {
    const data = await window.AppleID.auth.signIn();
    log.debug('Web Apple Sign-In result received');
    const authz = data?.authorization || {};
    const user = data?.user || {};
    return {
      identityToken: authz.id_token,
      email: user.email || null,
      name: joinName(user.name?.firstName, user.name?.lastName),
      isNative: false,
      // One-time code the backend exchanges for a revocable refresh token;
      // the exchange must echo the redirect URI from the authorize request.
      authorizationCode: authz.code || null,
      redirectUri: APPLE_AUTH_CONFIG.REDIRECT_URI,
    };
  } catch (error) {
    // Apple's popup throws on cancel — surface a friendly message
    const msg = error?.error === 'popup_closed_by_user' ? 'Sign-in cancelled' : (error?.error || 'Apple Sign-In failed');
    log.error('OAuth failed', error, { phase: 'web' });
    throw new Error(msg);
  }
};

export const isAppleAuthAvailable = async () => {
  if (isElectron() && window.electronAPI.appleOAuth) return true;
  const { Capacitor: Cap, SignInWithApple: SIWA } = await loadCapacitorModules();
  if (Cap && Cap.isNativePlatform() && SIWA) return true;
  return isWebSdkLoaded();
};

function joinName(first, last) {
  return [first, last].filter(Boolean).join(' ') || null;
}

function cryptoRandomState() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
